import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  IconButton,
  Modal
} from '@mui/material';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import DownloadIcon from '@mui/icons-material/Download';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import { colors } from '../../styles/colors';
import { DataGrid, GridColDef, GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { FileOutputLog, FileOutputLogFilter, getFileOutputLogs, downloadFileOutputLog } from '../../services/fileOutputLogService';
import { formatDateForDisplay, formatDateForApi, formatDateForDatePicker } from '../../utils/dateUtils';
import FileOutputLogDetail from './FileOutputLogDetail';

interface FileOutputLogTableProps {
  initialFilter?: {
    dateCreatedBySystemStart?: string;
    dateCreatedBySystemEnd?: string;
  };
}

const FileOutputLogTable = ({ initialFilter }: FileOutputLogTableProps) => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<FileOutputLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [selectedLog, setSelectedLog] = useState<FileOutputLog | null>(null);
  const [filter, setFilter] = useState<FileOutputLogFilter>(() => {
    if (initialFilter?.dateCreatedBySystemStart) {
      return {
        dateCreatedBySystemStart: initialFilter.dateCreatedBySystemStart,
        dateCreatedBySystemEnd: initialFilter.dateCreatedBySystemEnd
      };
    }
    return {};
  });

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0,
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([
    {
      field: 'dateCreatedBySystem',
      sort: 'desc',
    },
  ]);

  const columns: GridColDef[] = [
    {
      field: 'actions',
      headerName: 'Actions',
      width: 140,
      renderCell: (params: GridRenderCellParams<FileOutputLog>) => {
        return (
          <Box>
            <IconButton
              onClick={() => viewTransactions(params)}
              size="small"
              title="View Transactions in File"
            >
              <ReadMoreIcon />
            </IconButton>
            <IconButton
              onClick={() => handleDownload(params.row.id)}
              size="small"
              title="Download File"
            >
              <DownloadIcon />
            </IconButton>
          </Box>
        )
      }
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (params: GridRenderCellParams<FileOutputLog>) => {
        let color;

        switch (params.row.status.toLowerCase()) {
          case 'delivered':
            color = colors.success;
            break;
          case 'error':
            color = colors.error;
            break;
          case 'created':
            color = colors.pending;
            break;
          default:
            color = colors.pending;
        }

        return (
          <Box sx={{ color }}>
            {params.row.status}
          </Box>
        );
      }
    },
    {
      field: 'pbmName',
      headerName: 'PBM Name',
      width: 200,
      renderCell: (params: GridRenderCellParams<FileOutputLog>) => params.row.pbmConnection?.pbm?.pbmName || ''
    },
    {
      field: 'dateCreatedBySystem',
      headerName: 'Created Date',
      width: 200,
      renderCell: (params: GridRenderCellParams<FileOutputLog>) => formatDateForDisplay(params.row.dateCreatedBySystem)
    },
    {
      field: 'dateDeliveredToPBM',
      headerName: 'Delivered Date',
      width: 200,
      renderCell: (params: GridRenderCellParams<FileOutputLog>) => formatDateForDisplay(params.row.dateDeliveredToPBM)
    },
    { field: 'uaid', headerName: 'File Id', width: 130 },
    { field: 'description', headerName: 'Description', width: 600 }
  ];

  useEffect(() => {
    fetchLogs();
  }, [filter, sortModel]);

  const fetchLogs = async () => {
    try {
      const data = await getFileOutputLogs(filter);
      setLogs(data);
    } catch (error) {
      console.error('Failed to fetch logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const viewTransactions = (params: GridRenderCellParams<FileOutputLog>) => {
    navigate(`/transactions?uaid=${params.row.uaid}`);
  }

  const handleDownload = async (id: number) => {
    try {
      await downloadFileOutputLog(id);
    } catch (error) {
      console.error('Failed to download file:', error);
    }
  };

  const handleFilterChange = (field: keyof FileOutputLogFilter, value: string | null) => {
    if (field === 'uaid') {
      setSearchText(value || '');
      setFilter(prev => ({
        ...prev,
        [field]: (!value || value.length < 3) ? undefined : value
      }));
      return;
    }

    if (
      field === 'dateCreatedBySystemStart' ||
      field === 'dateCreatedBySystemEnd' ||
      field === 'dateDeliveredToPBMStart' ||
      field === 'dateDeliveredToPBMEnd'
    ) {
      
      setFilter(prev => ({
        ...prev,
        [field]: value ? formatDateForApi(value) : undefined
      }));
    } else {
      setFilter(prev => ({
        ...prev,
        [field]: value || undefined
      }));
    }
  };

  const clearFilters = () => {
    setSearchText('');
    setFilter({});
  };

  return (
    <Box>
      <Paper sx={{ p: 2 }}>
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Status</InputLabel>
            <Select
              value={filter.status || ''}
              label="Status"
              onChange={(e) => handleFilterChange('status', e.target.value)}
              size="small"
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="Delivered">Delivered</MenuItem>
              <MenuItem value="Failed">Failed</MenuItem>
              <MenuItem value="Created">Created</MenuItem>
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 120 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>PBM Name</InputLabel>
            <TextField
              size="small"
              value={filter.pbmName || ''}
              onChange={(e) => handleFilterChange('pbmName', e.target.value)}
            />
          </FormControl>

          <FormControl sx={{ minWidth: 220 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>File ID</InputLabel>
            <TextField
              size="small"
              value={searchText}
              onChange={(e) => handleFilterChange('uaid', e.target.value)}
              placeholder="Type 3 or more characters"
            />
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Created Date Range</InputLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateCreatedBySystemStart)}
                onChange={(e) => handleFilterChange('dateCreatedBySystemStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Start"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateCreatedBySystemEnd)}
                onChange={(e) => handleFilterChange('dateCreatedBySystemEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="End"
                sx={{ flex: 1 }}
              />
            </Box>
          </FormControl>

          <FormControl sx={{ minWidth: 280 }}>
            <InputLabel shrink sx={{ position: 'relative', transform: 'none', mb: 1 }}>Delivered Date Range</InputLabel>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateDeliveredToPBMStart)}
                onChange={(e) => handleFilterChange('dateDeliveredToPBMStart', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="Start"
                sx={{ flex: 1 }}
              />
              <TextField
                size="small"
                type="date"
                value={formatDateForDatePicker(filter.dateDeliveredToPBMEnd)}
                onChange={(e) => handleFilterChange('dateDeliveredToPBMEnd', e.target.value)}
                InputLabelProps={{ shrink: true }}
                placeholder="End"
                sx={{ flex: 1 }}
              />
            </Box>
          </FormControl>
          <IconButton
            onClick={clearFilters}
            size="small"
            title="Clear Filters"
            sx={{ mt: 4 }}
          >
            <FilterAltOffIcon />
          </IconButton>
        </Box>

        <DataGrid
          rows={logs}
          columns={columns}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[10, 25, 50]}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          sortingMode="server"
          autoHeight
          onRowClick={(params, event) => {
            // Ignore clicks on action buttons
            if ((event.target as HTMLElement).closest('.MuiIconButton-root')) {
              return;
            }
            setSelectedLog(params.row);
          }}
        />

        <Modal
          open={selectedLog !== null}
          onClose={() => setSelectedLog(null)}
          aria-labelledby="file-output-log-detail"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 2
          }}
        >
          <Box>
            {selectedLog && <FileOutputLogDetail log={selectedLog} />}
          </Box>
        </Modal>
      </Paper>
    </Box>
  );
};

export default FileOutputLogTable;
